import { ChangeDetectionStrategy, Component, forwardRef, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

/**
 * A single labelled date field backed by a Material datepicker, exposed as a
 * ControlValueAccessor so it drops into both `[(ngModel)]` and `formControlName`. The value is a
 * native `Date | null`.
 *
 * Unlike a plain text input — where `matDatepicker` has to sit on the native `<input>` and so
 * can't be wrapped (see `xemex-text-field` / `xemex-partner-select`) — the datepicker directives
 * live inside this component's own template, which is what makes a reusable date field possible.
 *
 * Provides its own native date adapter, so consumers don't need `provideNativeDateAdapter()`. The
 * display locale is inherited from an ancestor `MAT_DATE_LOCALE` provider when one is set.
 */
@Component({
  selector: 'xemex-date-field',
  standalone: true,
  imports: [MatFormFieldModule, MatInputModule, MatDatepickerModule],
  providers: [
    provideNativeDateAdapter(),
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateFieldComponent),
      multi: true,
    },
  ],
  styleUrl: './date-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './date-field.component.html',
})
export class DateFieldComponent implements ControlValueAccessor {
  /** Translated field label. */
  label = input.required<string>();
  placeholder = input('');
  min = input<Date | null>(null);
  max = input<Date | null>(null);
  /** Calendar-only by default: blocks typing so the date is picked from the popup. */
  readonly = input(true);

  protected readonly value = signal<Date | null>(null);
  protected readonly isDisabled = signal(false);

  private onChange: (value: Date | null) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: Date | null): void {
    this.value.set(value ?? null);
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  protected onDateChange(event: MatDatepickerInputEvent<Date>): void {
    this.value.set(event.value);
    this.onChange(event.value);
    this.onTouched();
  }
}
